import { useState, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface Term {
  id: number;
  title: string;
  content: string;
  version: string;
}

/**
 * 로그인했지만 필수 약관 동의 기록이 없는 사용자(이메일 확인 완료·OAuth 가입 포함)에게
 * 약관 동의를 받고, 동의 기록을 원자적으로 저장한다. 저장이 실패하면 성공으로 넘어가지 않는다.
 */
export default function TermsGate({ children }: { children: ReactNode }) {
  const { needsTermsConsent, recordAgreements, signOut } = useAuth();
  const location = useLocation();

  // 비밀번호 복구 경로(/reset-password)는 게이트에서 제외한다.
  // 새 약관 미동의 사용자도 유효한 복구 링크로 비밀번호를 재설정할 수 있어야 하므로,
  // 이 경로에서는 약관 동의 여부와 무관하게 통과시킨다. (복구 권한은 복구 세션 검증으로 유지)
  const isResetPasswordPath =
    location.pathname === '/reset-password' ||
    location.pathname.endsWith('/reset-password');
  const [terms, setTerms] = useState<Term[]>([]);
  const [checks, setChecks] = useState<boolean[]>([]);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [termsError, setTermsError] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!needsTermsConsent) return;
    let active = true;
    (async () => {
      setLoadingTerms(true);
      setTermsError('');
      try {
        const { data, error: fetchError } = await supabase
          .from('terms')
          .select('id, title, content, version')
          .eq('is_active', true)
          .order('id');
        if (!active) return;
        if (fetchError) throw fetchError;
        if (data && data.length > 0) {
          setTerms(data);
          setChecks(new Array(data.length).fill(false));
        } else {
          setTerms([]);
          setChecks([]);
          setTermsError('활성 약관이 없습니다. 관리자에게 문의해주세요.');
        }
      } catch {
        if (active) {
          setTermsError('약관을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
        }
      } finally {
        if (active) setLoadingTerms(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [needsTermsConsent, retryCount]);

  if (!needsTermsConsent || isResetPasswordPath) {
    return <>{children}</>;
  }

  const allChecked = checks.length > 0 && checks.every(Boolean);

  const handleToggle = (idx: number) => {
    const next = [...checks];
    next[idx] = !next[idx];
    setChecks(next);
  };

  const handleAllToggle = () => {
    const nextVal = !allChecked;
    setChecks(new Array(checks.length).fill(nextVal));
  };

  const handleAgree = async () => {
    if (!allChecked) return;
    setError('');
    setSaving(true);
    const result = await recordAgreements();
    setSaving(false);
    if (result.error) {
      setError('약관 동의 저장에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
    // 성공 시 hasAgreements가 갱신되어 게이트가 자동으로 해제된다.
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
        <div className="p-6 border-b border-background-200">
          <div className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center mb-4">
            <i className="ri-flask-line text-white text-sm"></i>
          </div>
          <h1 className="font-heading text-xl text-foreground-800 mb-1">약관 동의가 필요합니다</h1>
          <p className="text-foreground-400 text-sm">
            서비스를 계속 이용하려면 아래 필수 약관에 동의해주세요.
          </p>
        </div>

        {loadingTerms ? (
          <div className="p-6 text-center">
            <div className="w-8 h-8 mx-auto mb-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-foreground-400 text-sm">약관을 불러오는 중...</p>
          </div>
        ) : termsError ? (
          <div className="p-6 text-center">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-lg text-accent-600"></i>
            </div>
            <p className="text-foreground-500 text-sm">{termsError}</p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <button
                onClick={() => setRetryCount((c) => c + 1)}
                className="px-5 py-2 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                다시 시도
              </button>
              <button
                onClick={signOut}
                className="px-5 py-2 rounded-xl border border-background-300 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
              >
                로그아웃
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {terms.map((t, i) => (
              <div key={t.id} className="p-4 rounded-xl bg-background-50 border border-background-200">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <button
                    type="button"
                    onClick={() => handleToggle(i)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                      checks[i] ? 'bg-primary-500 border-primary-500' : 'border-background-300'
                    }`}
                  >
                    {checks[i] && <i className="ri-check-line text-white text-xs"></i>}
                  </button>
                  <span className="text-sm text-foreground-700">{t.title} (필수)</span>
                  <span className="text-xs text-foreground-300 ml-auto">{t.version}</span>
                </label>
                <div className="mt-3 max-h-40 overflow-y-auto text-xs text-foreground-500 whitespace-pre-line leading-relaxed pr-1">
                  {t.content}
                </div>
              </div>
            ))}

            <label className="flex items-center gap-2.5 cursor-pointer">
              <button
                type="button"
                onClick={handleAllToggle}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                  allChecked ? 'bg-primary-500 border-primary-500' : 'border-background-300'
                }`}
              >
                {allChecked && <i className="ri-check-line text-white text-xs"></i>}
              </button>
              <span className="text-sm text-foreground-700">전체 동의</span>
            </label>

            {error && (
              <div className="p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
                <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
                <p className="text-accent-700 text-sm">{error}</p>
              </div>
            )}

            <button
              onClick={handleAgree}
              disabled={!allChecked || saving}
              className="w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {saving ? '저장 중...' : '동의하고 계속하기'}
            </button>

            <button
              onClick={signOut}
              className="w-full py-2 text-center text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              로그아웃
            </button>
          </div>
        )}
      </div>
    </div>
  );
}